import React from 'react';
import { View } from 'react-native';
import { router } from 'expo-router';

import { ThemedText } from '@/components/themed-text';
import { PressableCard } from '@/components/ui/card';
import { formatDate } from '@/utils/format-date';

export interface SubscriptionQuotaSummary {
  enrollmentId: string;
  serviceName: string;
  quota: number;
  used: number;
  /** Início da próxima janela — quando o saldo volta a encher. */
  renewsAt: Date;
  periodWord: 'semana' | 'mês';
}

/**
 * Saldo da assinatura na home do cliente: quantas sessões da janela atual já
 * foram marcadas. É o que evita o cliente perder cota por esquecimento — a
 * cota não acumula de um período para o outro.
 */
export function SubscriptionQuotaCard({ summary }: { summary: SubscriptionQuotaSummary }) {
  const remaining = Math.max(summary.quota - summary.used, 0);
  const ratio = summary.quota > 0 ? Math.min(summary.used / summary.quota, 1) : 0;

  return (
    <PressableCard
      className="gap-3 p-4"
      onPress={() =>
        router.push({
          pathname: '/(tabs)/(client)/enrollments-client/[id]',
          params: { id: summary.enrollmentId },
        })
      }
    >
      <View className="flex-row items-start justify-between gap-3">
        <View className="flex-1 gap-0.5">
          <ThemedText className="font-semibold text-foreground" numberOfLines={2}>
            {summary.serviceName}
          </ThemedText>
          <ThemedText className="text-xs text-neutral-500">
            Assinatura · por {summary.periodWord}
          </ThemedText>
        </View>
        <View className="rounded-full bg-primary-50 px-2.5 py-1">
          <ThemedText
            className="text-xs font-bold text-primary"
            style={{ fontVariant: ['tabular-nums'] }}
          >
            {summary.used}/{summary.quota}
          </ThemedText>
        </View>
      </View>

      <View className="h-1.5 overflow-hidden rounded-full bg-neutral-100">
        <View className="h-full rounded-full bg-primary" style={{ width: `${ratio * 100}%` }} />
      </View>

      <ThemedText className="text-xs text-neutral-500">
        {remaining > 0
          ? `${remaining} ${remaining === 1 ? 'sessão sobrando' : 'sessões sobrando'} · renova ${formatDate(summary.renewsAt, "d 'de' MMM")}`
          : `Cota usada · renova ${formatDate(summary.renewsAt, "d 'de' MMM")}`}
      </ThemedText>
    </PressableCard>
  );
}
